import React, { Component } from "react";
import { api } from "../utils/api";
import toast from "react-hot-toast";
import { Eye, EyeOff, Lock, Mail, ArrowRight, TrendingUp, Hotel, ShieldCheck } from "lucide-react";

const BENEFITS = [
  { Icon: TrendingUp,  title: "Live GP on every booking",     desc: "Sale, purchase and margin update in real time as rates change." },
  { Icon: Hotel,       title: "One place for every partner",  desc: "Hotels, agents and rates, all searchable in seconds." },
  { Icon: ShieldCheck, title: "Approvals that route themselves", desc: "Loss bookings and negative-charge cases flag automatically." },
];

class LoginPage extends Component {
  constructor(props) {
    super(props);
    this.state = { email: "", password: "", showPass: false, loading: false, errors: {} };
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  handleChange(e) {
    const { name, value } = e.target;
    this.setState((prev) => ({ [name]: value, errors: { ...prev.errors, [name]: "" } }));
  }

  validate() {
    const { email, password } = this.state;
    const errors = {};
    if (!email) errors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = "Enter a valid email";
    if (!password) errors.password = "Password is required";
    return errors;
  }

  async handleSubmit(e) {
    e.preventDefault();
    const errors = this.validate();
    if (Object.keys(errors).length > 0) { this.setState({ errors }); return; }
    this.setState({ loading: true });
    try {
      const res = await api.post("/auth/login", { email: this.state.email, password: this.state.password });
      if (!res.data.status) {
        toast.error(res.data.message || "Invalid credentials");
        return;
      }
      localStorage.setItem("kts_token", res.data.data.token);
      localStorage.setItem("kts_user", JSON.stringify(res.data.data.user));
      toast.success(`Welcome, ${res.data.data.user.first_name}!`);
      if (this.props.onLogin) this.props.onLogin(res.data.data.user);
    } catch (err) {
      toast.error(err.response?.data?.message || "Login failed");
    } finally {
      this.setState({ loading: false });
    }
  }

  render() {
    const { email, password, showPass, loading, errors } = this.state;

    return (
      <div style={{ minHeight: "100vh", display: "flex", background: "#F4F5FA" }}>

        {/* ── Left panel — brand + benefits, hidden below 900px ── */}
        <div className="login-left" style={{ flex: "1 1 46%", background: "linear-gradient(160deg, #16213E 0%, #1E2D52 60%, #23315E 100%)", padding: "56px 56px", display: "flex", flexDirection: "column", justifyContent: "space-between", position: "relative", overflow: "hidden" }}>
          {/* decorative glow */}
          <div style={{ position: "absolute", top: -120, right: -100, width: 320, height: 320, borderRadius: "50%", background: "radial-gradient(circle, rgba(255,107,74,0.22), transparent 70%)" }} />
          <div style={{ position: "absolute", bottom: -160, left: -80, width: 360, height: 360, borderRadius: "50%", background: "radial-gradient(circle, rgba(201,168,76,0.14), transparent 70%)" }} />

          <div style={{ position: "relative" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: "linear-gradient(135deg, #C9A84C, #a8782c)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: "#0A0F1E", fontFamily: "Sora, sans-serif" }}>K</span>
              </div>
              <span style={{ fontSize: 18, fontWeight: 800, color: "#fff", fontFamily: "Sora, sans-serif" }}>
                KTS <span style={{ color: "#C9A84C", fontStyle: "italic", fontFamily: "Instrument Serif, serif", fontWeight: 400 }}>Booking</span>
              </span>
            </div>

            <h1 style={{ fontSize: 34, fontWeight: 800, color: "#fff", letterSpacing: "-0.02em", lineHeight: 1.2, marginTop: 48, maxWidth: 380 }}>
              Run bookings, not spreadsheets.
            </h1>
            <p style={{ fontSize: 14, color: "#9AA6C4", marginTop: 12, maxWidth: 360, lineHeight: 1.6 }}>
              Everything your team needs to sell, price and settle hotel bookings — in one CRM.
            </p>
          </div>

          <div style={{ position: "relative", display: "flex", flexDirection: "column", gap: 22 }}>
            {BENEFITS.map((b, i) => (
              <div key={i} style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(255,107,74,0.16)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <b.Icon size={17} color="#FF8A6E" />
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{b.title}</div>
                  <div style={{ fontSize: 12.5, color: "#8E99BE", marginTop: 3, lineHeight: 1.5 }}>{b.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <p style={{ position: "relative", fontSize: 11, color: "#5C6890", letterSpacing: "0.04em" }}>
            KTS Booking System © {new Date().getFullYear()}
          </p>
        </div>

        {/* ── Right panel — sign in form ── */}
        <div style={{ flex: "1 1 54%", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div style={{ width: "100%", maxWidth: 380 }}>

            <div className="login-left-hidden-on-desktop" style={{ textAlign: "center", marginBottom: 28 }}>
              <div style={{ width: 52, height: 52, margin: "0 auto 12px", background: "linear-gradient(135deg, #C9A84C, #a8782c)", borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 8px 32px rgba(201,168,76,0.35)" }}>
                <span style={{ fontSize: 22, fontWeight: 800, color: "#0A0F1E", fontFamily: "Sora, sans-serif" }}>K</span>
              </div>
              <h1 style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>
                KTS <span style={{ color: "#C9A84C", fontStyle: "italic", fontFamily: "Instrument Serif, serif", fontWeight: 400 }}>Booking</span>
              </h1>
            </div>

            <h2 style={{ fontSize: 22, fontWeight: 800, color: "#16213E", letterSpacing: "-0.01em" }}>Sign in</h2>
            <p style={{ fontSize: 13, color: "#6B7280", marginTop: 5, marginBottom: 28 }}>Enter your credentials to access the system</p>

            <form onSubmit={this.handleSubmit} noValidate>
              <div className="field-group" style={{ marginBottom: 18 }}>
                <label className="login-label">Email Address</label>
                <div className="login-input-wrap">
                  <Mail size={15} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#9CA3AF", pointerEvents: "none", zIndex: 1 }} />
                  <input
                    type="email" name="email" placeholder="admin@kts.com" value={email}
                    onChange={this.handleChange} className="login-input"
                    style={{ paddingLeft: 42, ...(errors.email ? { borderColor: "#E1483A" } : {}) }}
                    autoFocus autoComplete="email"
                  />
                </div>
                {errors.email && <p style={{ fontSize: 11, color: "#E1483A", marginTop: 4 }}>{errors.email}</p>}
              </div>

              <div className="field-group" style={{ marginBottom: 24 }}>
                <label className="login-label">Password</label>
                <div className="login-input-wrap">
                  <Lock size={15} style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#9CA3AF", pointerEvents: "none", zIndex: 1 }} />
                  <input
                    type={showPass ? "text" : "password"} name="password" placeholder="Enter your password" value={password}
                    onChange={this.handleChange} className="login-input"
                    style={{ paddingLeft: 42, paddingRight: 44, ...(errors.password ? { borderColor: "#E1483A" } : {}) }}
                    autoComplete="current-password"
                  />
                  <button type="button" tabIndex={-1} onClick={() => this.setState((prev) => ({ showPass: !prev.showPass }))}
                    style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", padding: 4 }}>
                    {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {errors.password && <p style={{ fontSize: 11, color: "#E1483A", marginTop: 4 }}>{errors.password}</p>}
              </div>

              <button
                type="submit" disabled={loading}
                style={{
                  width: "100%", padding: "13px 20px",
                  background: loading ? "#F0A18E" : "linear-gradient(135deg, #FF6B4A, #E85A3A)",
                  border: "none", borderRadius: 14, fontFamily: "Sora, sans-serif", fontSize: 14, fontWeight: 700, color: "#fff",
                  cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                  transition: "all 0.2s", boxShadow: loading ? "none" : "0 6px 20px rgba(255,107,74,0.32)",
                }}
                onMouseEnter={(e) => { if (!loading) e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; }}
              >
                {loading ? (
                  <><span style={{ display: "inline-block", animation: "spin 1s linear infinite", width: 16, height: 16, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%" }} /> Signing in…</>
                ) : (
                  <>Sign In <ArrowRight size={15} /></>
                )}
              </button>
            </form>

            <p className="login-left-hidden-on-desktop" style={{ textAlign: "center", fontSize: 12, color: "#9CA3AF", marginTop: 24 }}>
              KTS Booking System © {new Date().getFullYear()}
            </p>
          </div>
        </div>

        <style>{`
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          .login-left-hidden-on-desktop { display: none; }
          @media (max-width: 900px) {
            .login-left { display: none; }
            .login-left-hidden-on-desktop { display: block; }
          }
        `}</style>
      </div>
    );
  }
}

export default LoginPage;